<script lang="ts">
  import { queryFlow } from '../lib/analytics/flow';
  import {
    queryCategorizedBalance,
    type CategorizedBalanceQuery,
  } from '../lib/analytics/categorized-balance';
  import { queryStock } from '../lib/analytics/stock';
  import Chart from '../lib/charts/Chart.svelte';
  import ChartCard from '../lib/charts/ChartCard.svelte';
  import { toCompositionOption, toTimeChartOption } from '../lib/charts/options';
  import type { DataStore } from '../lib/data/store';
  import type { FlowQuery, StockQuery, YearMonth } from '../lib/data/types';
  import FilterBar from '../lib/filters/FilterBar.svelte';
  import { formatCents, formatMonth, signedClass } from '../lib/presentation/format';
  import { isLiabilityGroup, selectedAccountMonths } from '../lib/presentation/insights';
  import { labelFlowSeries, labelStockSeries } from '../lib/presentation/series';
  import type { AppState } from '../lib/state/app.svelte';
  import EmptyState from '../lib/ui/EmptyState.svelte';
  import InlineError from '../lib/ui/InlineError.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import Skeleton from '../lib/ui/Skeleton.svelte';

  let {
    store,
    state,
    transactionsState,
    transactionsError,
    retryTransactions,
  }: {
    store: DataStore;
    state: AppState;
    transactionsState: 'idle' | 'loading' | 'ready' | 'error';
    transactionsError: string;
    retryTransactions: () => void;
  } = $props();

  function stockQuery(
    metric: StockQuery['metric'],
    breakdown: StockQuery['breakdown'],
  ): StockQuery {
    return {
      metric,
      breakdown,
      visiblePeriod: {
        from: state.fromMonth ?? store.months[0]!,
        to: state.toMonth ?? store.months.at(-1)!,
      },
      ...(state.accountFilterActive ? { accountIds: new Set(state.accountIds) } : {}),
    };
  }
  let balanceQuery = $derived(stockQuery('closing-balance', 'account'));
  let balance = $derived(
    labelStockSeries(
      queryStock(store.core.account_month, store.months, balanceQuery),
      balanceQuery,
      store,
    ),
  );
  let totalBalanceQuery = $derived(stockQuery('closing-balance', 'none'));
  let totalBalance = $derived(
    labelStockSeries(
      queryStock(store.core.account_month, store.months, totalBalanceQuery),
      totalBalanceQuery,
      store,
    ),
  );
  function categorizedQuery(
    breakdown: CategorizedBalanceQuery['breakdown'],
  ): CategorizedBalanceQuery {
    return {
      breakdown,
      ...(state.accountFilterActive ? { accountIds: new Set(state.accountIds) } : {}),
      categoryIds: new Set(state.categoryIds),
      includeUncategorized: state.includeUncategorized,
      visiblePeriod: {
        from: state.fromMonth ?? store.months[0]!,
        to: state.toMonth ?? store.months.at(-1)!,
      },
    };
  }
  let categorizedTotalBalance = $derived.by(() => {
    if (!state.categoryFilterActive || transactionsState !== 'ready') return null;
    return labelStockSeries(
      queryCategorizedBalance(
        store.core.account_month,
        store.transactions,
        store.months,
        categorizedQuery('none'),
      ),
      totalBalanceQuery,
      store,
    );
  });
  let categorizedAccountBalance = $derived.by(() => {
    if (!state.categoryFilterActive || transactionsState !== 'ready') return null;
    return labelStockSeries(
      queryCategorizedBalance(
        store.core.account_month,
        store.transactions,
        store.months,
        categorizedQuery('account'),
      ),
      balanceQuery,
      store,
    );
  });
  let visibleTotalBalance = $derived(categorizedTotalBalance ?? totalBalance);
  let visibleAccountBalance = $derived(categorizedAccountBalance ?? balance);
  let deltaQuery = $derived(stockQuery('delta', 'account'));
  let deltas = $derived(
    labelStockSeries(
      queryStock(store.core.account_month, store.months, deltaQuery),
      deltaQuery,
      store,
    ),
  );
  let finalMonth = $derived((balance.buckets.at(-1) ?? store.months.at(-1))! as YearMonth);
  let finalRows = $derived(
    selectedAccountMonths(
      store,
      finalMonth,
      state.accountFilterActive ? state.accountIds : undefined,
    ).sort((a, b) => Math.abs(b.closing_balance_cents) - Math.abs(a.closing_balance_cents)),
  );
  function groupRows(rows: typeof finalRows) {
    const rowsByGroup = new Map<string, typeof finalRows>();
    for (const row of rows) {
      const rows = rowsByGroup.get(row.account_group_id) ?? [];
      rows.push(row);
      rowsByGroup.set(row.account_group_id, rows);
    }

    return store.core.account_groups.flatMap((group) => {
      const rows = rowsByGroup.get(group.id);
      if (!rows?.length) return [];
      return [
        {
          id: group.id,
          name: group.name,
          rows,
          total: rows.reduce((sum, row) => sum + row.closing_balance_cents, 0),
          absoluteTotal: rows.reduce((sum, row) => sum + Math.abs(row.closing_balance_cents), 0),
        },
      ];
    });
  }
  let accountSections = $derived.by(() => {
    const sections = [
      {
        id: 'assets',
        title: 'Conti attivi',
        description: 'Disponibilità, investimenti e crediti',
        rows: finalRows.filter((row) => !isLiabilityGroup(store, row.account_group_id)),
      },
      {
        id: 'liabilities',
        title: 'Conti passivi',
        description: 'Debiti e linee di credito',
        rows: finalRows.filter((row) => isLiabilityGroup(store, row.account_group_id)),
      },
    ];
    return sections
      .filter((section) => section.rows.length > 0)
      .map((section) => ({
        ...section,
        groups: groupRows(section.rows),
        total: section.rows.reduce((sum, row) => sum + row.closing_balance_cents, 0),
      }));
  });
  let total = $derived(finalRows.reduce((sum, row) => sum + row.closing_balance_cents, 0));
  let activeRows = $derived(
    finalRows
      .filter((row) => !isLiabilityGroup(store, row.account_group_id))
      .map((row) => ({
        name: store.accountLabel(row.account_id),
        value: row.closing_balance_cents,
        colorKey: `account:${row.account_id}`,
      })),
  );
  let passiveRows = $derived(
    finalRows
      .filter((row) => isLiabilityGroup(store, row.account_group_id))
      .map((row) => ({
        name: store.accountLabel(row.account_id),
        value: row.closing_balance_cents,
        colorKey: `account:${row.account_id}`,
      })),
  );
  let movementPeriod = $derived.by(() => {
    const fromMonth = state.fromMonth ?? store.months[0]!;
    const toMonth = state.toMonth ?? store.months.at(-1)!;
    const firstDay = store.core.calendar.find((day) => day.month >= fromMonth);
    const lastDay = store.core.calendar.filter((day) => day.month <= toMonth).at(-1);
    return {
      from: firstDay?.date ?? store.core.calendar[0]!.date,
      to: lastDay?.date ?? store.core.calendar.at(-1)!.date,
    };
  });
  let movementQuery = $derived.by((): FlowQuery => ({
    metric: 'net',
    grain: 'day',
    breakdown: 'account',
    filters: {
      ...(state.accountFilterActive ? { accountIds: new Set(state.accountIds) } : {}),
      ...(state.categoryFilterActive
        ? {
            categoryIds: new Set(state.categoryIds),
            includeUncategorized: state.includeUncategorized,
          }
        : {}),
    },
    visiblePeriod: movementPeriod,
  }));
  let movements = $derived(
    transactionsState === 'ready'
      ? labelFlowSeries(
          queryFlow(store.transactions, store.core.calendar, movementQuery),
          movementQuery,
          store,
        )
      : null,
  );
  let movementCountQuery = $derived.by((): FlowQuery => ({
    ...movementQuery,
    metric: 'count',
    breakdown: 'none',
  }));
  let movementCount = $derived(
    transactionsState === 'ready'
      ? (queryFlow(store.transactions, store.core.calendar, movementCountQuery).total ?? 0)
      : null,
  );
  let movementTotal = $derived(movements?.total ?? 0);

  function selectAccount(id: string): void {
    state.accountIds = [id];
    state.accountFilterActive = true;
    state.syncUrl();
  }

  function scrollToMovements(): void {
    document.querySelector('#account-movements')?.scrollIntoView({ behavior: 'smooth' });
  }

  function compositionHeight(rowCount: number): string {
    return `${Math.max(11, rowCount * 2 + 3)}rem`;
  }
</script>

<main id="main-content" class="page">
  <PageHeader
    title="Account"
    description="Confronta saldi e variazioni dei conti; i movimenti giornalieri arrivano in modo progressivo."
    {state}
  />
  <FilterBar {state} />

  {#if state.categoryFilterActive}
    <section class="account-filter-impact" aria-live="polite">
      <div>
        <p class="eyebrow">Categorie applicate ai movimenti</p>
        <strong>
          {movementCount === null
            ? 'Calcolo dei movimenti…'
            : `${movementCount} ${movementCount === 1 ? 'movimento trovato' : 'movimenti trovati'}`}
        </strong>
        <p>
          I saldi contabili restano invariati; il filtro categoria agisce sul flusso giornaliero.
        </p>
      </div>
      <div class="account-filter-impact-value">
        <span>Flusso netto filtrato</span>
        <strong class={signedClass(movementTotal)}>{formatCents(movementTotal)}</strong>
        <button class="text-button" type="button" onclick={scrollToMovements}>Vedi grafico</button>
      </div>
    </section>
  {/if}

  <div class="page-grid">
    <section class="account-overview span-12">
      <div class="account-overview-header">
        <div>
          <p class="eyebrow">Posizione al {formatMonth(finalMonth)}</p>
          <h2>Il tuo registro dei conti</h2>
          <p class="muted">{finalRows.length} conti, organizzati per natura e disponibilità.</p>
        </div>
        <div class="account-overview-total">
          <span>Saldo complessivo</span>
          <strong>{formatCents(total)}</strong>
        </div>
      </div>
      {#if finalRows.length}
        <div class="account-kind-list">
          {#each accountSections as section (section.id)}
            <section
              class="account-kind account-kind-{section.id}"
              aria-labelledby={`account-kind-${section.id}`}
            >
              <header class="account-kind-header">
                <div>
                  <h3 id={`account-kind-${section.id}`}>{section.title}</h3>
                  <p class="muted">
                    {section.description} · {section.rows.length}
                    {section.rows.length === 1 ? 'conto' : 'conti'}
                  </p>
                </div>
                <strong class={signedClass(section.total)}>{formatCents(section.total)}</strong>
              </header>
              <div class="account-kind-groups">
                {#each section.groups as group (group.id)}
                  <section class="account-group" aria-labelledby={`account-group-${group.id}`}>
                    <header class="account-group-header">
                      <div>
                        <h4 id={`account-group-${group.id}`}>{group.name}</h4>
                        <p class="muted">
                          {group.rows.length}
                          {group.rows.length === 1 ? 'conto' : 'conti'}
                        </p>
                      </div>
                      <strong>{formatCents(group.total)}</strong>
                    </header>
                    <div class="account-summary-grid">
                      {#each group.rows as row (row.account_id)}
                        <button
                          class="account-summary"
                          type="button"
                          onclick={() => selectAccount(row.account_id)}
                        >
                          <span class="account-name">{store.accountLabel(row.account_id)}</span>
                          <strong>{formatCents(row.closing_balance_cents)}</strong>
                          <small class={signedClass(row.delta_cents)}
                            >{formatCents(row.delta_cents)} nel mese</small
                          >
                          <span class="account-share"
                            >{group.absoluteTotal === 0
                              ? '—'
                              : new Intl.NumberFormat('it-IT', {
                                  style: 'percent',
                                  maximumFractionDigits: 1,
                                }).format(
                                  Math.abs(row.closing_balance_cents) / group.absoluteTotal,
                                )} del gruppo</span
                          >
                        </button>
                      {/each}
                    </div>
                  </section>
                {/each}
              </div>
            </section>
          {/each}
        </div>
      {:else}<EmptyState action={() => state.resetFilters()} />{/if}
    </section>

    <div class="span-12">
      <ChartCard
        title="Saldo totale"
        description={state.categoryFilterActive
          ? 'Saldo iniziale reale più il cumulativo dei movimenti che rispettano insieme conti e categorie; trasferimenti esclusi.'
          : 'Somma mensile dei saldi dei conti selezionati.'}
        result={visibleTotalBalance}
        option={toTimeChartOption(visibleTotalBalance, 'area')}
        height="21rem"
      />
    </div>
    <div class="span-12">
      <ChartCard
        title="Saldo per conto"
        description={state.categoryFilterActive
          ? 'Saldo ricostruito per ogni conto usando soltanto i movimenti delle categorie selezionate.'
          : 'Serie cumulate e allineate sul calendario mensile.'}
        result={visibleAccountBalance}
        option={toTimeChartOption(visibleAccountBalance, 'line')}
        height="23rem"
      />
    </div>
    <div class="span-12">
      <ChartCard
        title="Variazione mensile"
        description="Movimenti netti per singolo conto."
        result={deltas}
        option={toTimeChartOption(deltas, 'bar')}
        height="20rem"
      />
    </div>
    <section class="card account-composition span-12">
      <div class="card-header">
        <div>
          <h2>Attività e passività</h2>
          <p class="muted">Classificazione stabile del gruppo.</p>
        </div>
      </div>
      <div class="split-charts">
        <div>
          <h3>Attività</h3>
          <Chart
            option={toCompositionOption(activeRows, 'bar', { tooltip: false })}
            ariaLabel="Distribuzione conti attivi"
            height={compositionHeight(activeRows.length)}
          />
        </div>
        <div>
          <h3>Passività</h3>
          <Chart
            option={toCompositionOption(passiveRows, 'bar', { tooltip: false })}
            ariaLabel="Distribuzione conti passivi"
            height={compositionHeight(passiveRows.length)}
          />
        </div>
      </div>
    </section>

    <section
      id="account-movements"
      class="span-12 flow-region"
      aria-busy={transactionsState === 'loading'}
    >
      <div class="section-heading">
        <div>
          <p class="eyebrow">Caricamento progressivo</p>
          <h2>Movimenti giornalieri</h2>
          <p class="muted">
            Questa sezione usa il bundle transazioni; i saldi sopra restano immediatamente
            disponibili.
          </p>
        </div>
      </div>
      {#if transactionsState === 'ready' && movements}
        <ChartCard
          title="Flusso netto giornaliero"
          description="Entrate meno spese; i trasferimenti sono esclusi."
          result={movements}
          option={toTimeChartOption(movements, 'bar')}
          height="20rem"
        />
      {:else if transactionsState === 'error'}
        <InlineError message={transactionsError} retry={retryTransactions} />
      {:else}
        <Skeleton height="24rem" label="Caricamento movimenti dei conti" />
      {/if}
    </section>
  </div>
</main>
